import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, skip, switchMap } from 'rxjs/operators'; 
import { ContractWorkflowService, WorkflowTaskDto } from './contract-workflow.service';

@Component({
  selector: 'app-contract-workflow-list',
  template: `
    <div class="cwl">

      <!-- ── Header ──────────────────────────────────────────── -->
      <div class="cwl__header">
        <div class="cwl__header-left">
          <span class="cwl__title">{{ title }}</span>
          <span class="cwl__subtitle">{{ subtitleText }}</span>
        </div>
        <button class="cwl__refresh" (click)="loadTasks()" title="Refresh">
          <mat-icon [class.cwl__spinning]="loading">refresh</mat-icon>
        </button>
      </div>

      <!-- ── Loading ─────────────────────────────────────────── -->
      <div class="cwl__loading" *ngIf="loading">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading…</span>
      </div>

      <!-- ── Empty ───────────────────────────────────────────── -->
      <div class="cwl__empty" *ngIf="!loading && tasks.length === 0">
        <mat-icon>inbox</mat-icon>
        <p>No pending {{ taskTypeLabel }} tasks</p>
      </div>

      <!-- ── Table ───────────────────────────────────────────── -->
      <table class="cwl__table" *ngIf="!loading && tasks.length > 0">
        <thead>
          <tr>
            <th>CONTRACT NO.</th>
            <th>TITLE</th>
            <th>SUPPLIER</th>
            <th>PROGRESS</th>
            <th>DUE DATE</th>
            <th>STATUS</th>
            <th>ACTION</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let task of tasks"
              [class.cwl__row--overdue]="task.overdue"
              (click)="openTask(task)">

            <!-- Contract No -->
            <td class="cwl__td-num">
              {{ task.contractNumber || '—' }}
            </td>

            <!-- Title -->
            <td class="cwl__td-title">
              <span class="cwl__title-text">{{ task.contractTitle || '(No title)' }}</span>
            </td>

            <!-- Supplier -->
            <td class="cwl__td-supplier">
              {{ task.supplierName || '—' }}
            </td>

            <!-- Progress -->
            <td class="cwl__td-progress">
              <ng-container *ngIf="taskType === 'REVIEW' && task.reviewTotal > 0">
                <div class="cwl__bar">
                  <div class="cwl__bar-fill"
                       [style.width.%]="(task.reviewStep / task.reviewTotal) * 100">
                  </div>
                </div>
                <span class="cwl__bar-label">
                  {{ task.reviewStep + 1 }}/{{ task.reviewTotal }}
                </span>
              </ng-container>
              <ng-container *ngIf="taskType !== 'REVIEW'">—</ng-container>
            </td>

            <!-- Due Date -->
            <td class="cwl__td-date" [class.cwl__td-date--overdue]="task.overdue">
              <mat-icon *ngIf="task.overdue" class="cwl__warn-icon">warning</mat-icon>
              {{ task.dueDate ? (task.dueDate | date:'d MMM yyyy') : '—' }}
            </td>

            <!-- Status badge -->
            <td class="cwl__td-status">
              <span class="cwl__badge" [ngClass]="badgeClass(task)">
                {{ task.overdue ? 'Overdue' : badgeLabel }}
              </span>
            </td>

            <!-- Action -->
            <td class="cwl__td-action" (click)="$event.stopPropagation()">
              <button class="cwl__view-btn" (click)="openTask(task)" title="Open task">
                <mat-icon>visibility</mat-icon>
              </button>
            </td>

          </tr>
        </tbody>
      </table>

    </div>
  `,
  styles: [`
    .cwl {
      background: #fff;
      border-radius: 8px;
      overflow: hidden;
    }

    /* Header */
    .cwl__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 20px 10px;
      border-bottom: 1px solid #e8edf5;
    }
    .cwl__header-left { display: flex; flex-direction: column; gap: 2px; }
    .cwl__title { font-size: 14px; font-weight: 700; color: #1B3A6B; }
    .cwl__subtitle { font-size: 11px; color: #7a8fa6; }
    .cwl__refresh {
      background: none; border: none; cursor: pointer;
      color: #7a8fa6; padding: 4px; border-radius: 4px;
      display: flex; align-items: center;
    }
    .cwl__refresh:hover { color: #1B3A6B; background: #f0f4fb; }
    .cwl__spinning { animation: cwl-spin 1s linear infinite; }
    @keyframes cwl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    /* Loading */
    .cwl__loading {
      display: flex; align-items: center; gap: 10px;
      padding: 24px 20px; color: #7a8fa6; font-size: 13px;
    }

    /* Empty */
    .cwl__empty {
      display: flex; flex-direction: column; align-items: center;
      padding: 40px 20px; color: #aab5c5; text-align: center;
    }
    .cwl__empty mat-icon {
      font-size: 40px; width: 40px; height: 40px;
      color: #d0daea; margin-bottom: 8px;
    }
    .cwl__empty p { font-size: 13px; margin: 0; }

    /* Table */
    .cwl__table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    .cwl__table thead tr {
      background: #f4f7fb;
      border-bottom: 2px solid #e0e8f4;
    }
    .cwl__table th {
      padding: 10px 16px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: #7a8fa6;
      letter-spacing: 0.4px;
      white-space: nowrap;
    }
    .cwl__table tbody tr {
      border-bottom: 1px solid #f0f4fa;
      cursor: pointer;
      transition: background 0.12s;
    }
    .cwl__table tbody tr:hover { background: #f8fafd; }
    .cwl__table tbody tr:last-child { border-bottom: none; }
    .cwl__table td {
      padding: 12px 16px;
      vertical-align: middle;
      color: #2d3748;
    }

    /* Overdue row highlight */
    .cwl__row--overdue { background: #fff9f9 !important; }

    /* Column-specific */
    .cwl__td-num {
      font-weight: 700;
      color: #4A90D9;
      font-family: monospace;
      font-size: 12px;
      white-space: nowrap;
    }
    .cwl__td-title { max-width: 280px; }
    .cwl__title-text {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 500;
      color: #1a1a2e;
    }
    .cwl__td-supplier {
      color: #555;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 160px;
    }
    .cwl__td-progress { white-space: nowrap; min-width: 110px; }
    .cwl__bar {
      height: 4px; background: #e0e8f4; border-radius: 2px;
      overflow: hidden; margin-bottom: 3px; width: 80px;
    }
    .cwl__bar-fill {
      height: 100%; background: #4A90D9;
      border-radius: 2px; transition: width .3s;
    }
    .cwl__bar-label { font-size: 10px; color: #7a8fa6; }

    .cwl__td-date {
      white-space: nowrap; color: #555; font-size: 12px;
    }
    .cwl__td-date--overdue { color: #dc3545; font-weight: 600; }
    .cwl__warn-icon {
      font-size: 13px; width: 13px; height: 13px;
      color: #dc3545; vertical-align: middle; margin-right: 3px;
    }

    .cwl__td-status { white-space: nowrap; }
    .cwl__badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 600;
    }
    .cwl__badge--review    { background: #EBF3FB; color: #1B3A6B; }
    .cwl__badge--approval  { background: #d4edda; color: #155724; }
    .cwl__badge--archive   { background: #e2e3e5; color: #383d41; }
    .cwl__badge--configure { background: #fff3cd; color: #856404; }
    .cwl__badge--overdue   { background: #f8d7da; color: #721c24; }

    .cwl__td-action { text-align: center; width: 60px; }
    .cwl__view-btn {
      background: none;
      border: 1px solid #e0e8f4;
      border-radius: 6px;
      padding: 5px 8px;
      cursor: pointer;
      color: #4A90D9;
      display: inline-flex;
      align-items: center;
      transition: all .12s;
    }
    .cwl__view-btn:hover {
      background: #4A90D9;
      color: #fff;
      border-color: #4A90D9;
    }
    .cwl__view-btn mat-icon { font-size: 16px; width: 16px; height: 16px; }
  `]
})
export class ContractWorkflowListComponent implements OnInit, OnChanges, OnDestroy {

  @Input() taskType: 'REVIEW' | 'APPROVAL' | 'ARCHIVE' | 'CONFIGURE' = 'REVIEW';
  @Input() title = 'Contract Tasks';

  tasks: WorkflowTaskDto[] = [];
  loading = false;

  private destroy$ = new Subject<void>();
  // Inner subject used to cancel prior loadTasks() calls via switchMap
  private reload$ = new Subject<void>();

  get taskTypeLabel(): string {
    return { REVIEW: 'review', APPROVAL: 'approval', ARCHIVE: 'archive', CONFIGURE: 'configure' }
      [this.taskType] ?? '';
  }

  get subtitleText(): string {
    return {
      REVIEW:    'Contracts awaiting your review',
      APPROVAL:  'Contracts pending your signature',
      ARCHIVE:   'Contracts ready to archive',
      CONFIGURE: 'Workflows pending routing confirmation'
    }[this.taskType] ?? '';
  }

  get badgeLabel(): string {
    return {
      REVIEW:    'In Review',
      APPROVAL:  'Pending Signature',
      ARCHIVE:   'Ready to Archive',
      CONFIGURE: 'Needs Routing'
    }[this.taskType] ?? this.taskType;
  }

  badgeClass(task: WorkflowTaskDto): string {
    if (task.overdue) return 'cwl__badge--overdue';
    return {
      REVIEW:    'cwl__badge--review',
      APPROVAL:  'cwl__badge--approval',
      ARCHIVE:   'cwl__badge--archive',
      CONFIGURE: 'cwl__badge--configure'
    }[this.taskType] ?? 'cwl__badge--review';
  }

  constructor(
    private workflowService: ContractWorkflowService,
    private router: Router
  ) {}

  // ── React to parent changing [taskType] input ─────────────────
  // When the user clicks REVIEW → APPROVAL → REVIEW in the sidebar,
  // the parent updates contractTaskType and Angular fires ngOnChanges.
  // Without this, the component stays mounted and shows stale data
  // because ngOnInit only runs once on first render.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskType'] && !changes['taskType'].firstChange) {
      this.reload$.next();
    }
  }

  ngOnInit(): void {
    // ── FIX: Use switchMap so rapid calls cancel prior in-flight requests ──
    // reload$ emits whenever we want to (re)load tasks.
    // switchMap cancels any previous HTTP call before starting the next one,
    // preventing race conditions where an older empty result overwrites a newer
    // correct result.
    this.reload$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          this.loading = true;
          return this.workflowService.getMyTasks(this.taskType);
        })
      )
      .subscribe({
        next:  tasks => { this.tasks = tasks; this.loading = false; },
        error: err   => { console.error('Failed to load tasks', err); this.loading = false; }
      });

    // Trigger initial load
    this.reload$.next();

    // ── FIX: skip(1) prevents the IMMEDIATE BehaviorSubject emit on subscribe ──
    // Without skip(1), subscribing to the BehaviorSubject immediately fires
    // loadTasks() again (before the first HTTP response arrives), creating two
    // races. skip(1) means this subscription only reacts to future emits
    // (i.e., when refreshTaskCounts() is called after a task completes).
    this.workflowService.taskCounts$
      .pipe(
        skip(1),                     // ← ignore the initial BehaviorSubject emit
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.reload$.next());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  loadTasks(): void {
    // Public method — called from the refresh button in the template
    this.reload$.next();
  }

  openTask(task: WorkflowTaskDto): void {
    this.router.navigate(['/workflow/contract-task', task.taskId]);
  }
}